import { useEffect, useMemo, useState } from 'react'
import { Drawer, Field, notify } from '@/kit/ui'
import { batchesOfProduct, compareVersions, isValidVersion } from '@/lib/batch'
import { batchOf, flowNameFor, nextFlowVersion } from '@/lib/flow'
import { endpointLabel } from '@/lib/endpoints'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import type { Lender, WorkflowVersion } from '@/types'

/** New workflow (F3-2): product, name, version, which batch, and start from blank (chosen endpoints in a row) or a copy. */
export function NewWorkflowDrawer({ lender, isOpen, onClose, onCreated }: { lender: Lender; isOpen: boolean; onClose: () => void; onCreated: (f: WorkflowVersion) => void }) {
  const { user } = useCurrentUser()
  const flows = useFlowStore((s) => s.flows)
  const batches = useCodeStore((s) => s.batches)
  const apis = useCodeStore((s) => s.apis)
  const mineFlows = useMemo(() => flows.filter((f) => f.lenderSlug === lender.slug && f.status !== 'retired'), [flows, lender.slug])
  const [productSlug, setProductSlug] = useState(lender.products[0]?.slug ?? '')
  // BP-1: the chosen product's batch (else the lender's whole-lender batch of before)
  const mineBatches = useMemo(() => batchesOfProduct(batches, lender.slug, productSlug || null).filter((b) => b.status !== 'retired').sort((a, b) => compareVersions(b.version, a.version)), [batches, lender.slug, productSlug])
  const live = mineBatches.find((b) => b.status === 'live')
  const tokenRoutes = apis.filter((a) => a.lenderSlug === lender.slug && a.auth === 'token_source').map((a) => a.endpoint)

  const [name, setName] = useState('')
  const [version, setVersion] = useState('1.0.0')
  const [batchSel, setBatchSel] = useState('live')
  const [start, setStart] = useState<'blank' | 'copy'>('copy')
  const [fromId, setFromId] = useState('')
  const [files, setFiles] = useState<string[]>([])
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (!isOpen) return
    const p = lender.products[0]
    setProductSlug(p?.slug ?? '')
    const n = p ? flowNameFor(lender.slug, p) : `${lender.slug}.flow`
    setName(n)
    setVersion(nextFlowVersion(flows, n))
    setBatchSel(live ? 'live' : mineBatches[0]?.version ?? 'live')
    const copyable = mineFlows.find((f) => f.status === 'live') ?? mineFlows[0]
    setStart(copyable ? 'copy' : 'blank')
    setFromId(copyable?.id ?? '')
    setFiles([])
    setError(undefined)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const pin: WorkflowVersion['batch'] = batchSel === 'live' ? { mode: 'live' } : { mode: 'pinned', version: batchSel }
  const batch = batchOf({ lenderSlug: lender.slug, productSlug, batch: pin }, batches)
  const oldRoutes = apis.filter((a) => a.lenderSlug === lender.slug && a.status !== 'active').map((a) => a.endpoint)
  const callable = (batch?.manifest.endpoints ?? []).filter((e) => !e.tokenSource && !tokenRoutes.includes(e.path) && !oldRoutes.includes(e.path)).map((e) => e.path)

  const submit = () => {
    try {
      if (!isValidVersion(version)) throw new Error('The version must look like 1.0.0')
      if (!/^[a-z0-9]+\.[a-z0-9-]+\.flow$/.test(name)) throw new Error('The name looks like <lender>.<product>.flow, e.g. moneyview.pl.flow')
      if (start === 'copy' && !fromId) throw new Error('Pick the version to copy')
      const f = useFlowStore.getState().newWorkflow({ lenderSlug: lender.slug, productSlug, name, version, batch: pin, start: start === 'copy' ? { kind: 'copy', fromId } : { kind: 'blank', files } }, user.email)
      notify.success(`${name} ${version} created`, 'A draft: change it on the canvas, Save, Run all paths.')
      onClose()
      onCreated(f)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="New workflow"
      subtitle={`${lender.name} · a draft version on the canvas`}
      footer={<div className="flex justify-end gap-2"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={submit} data-testid="create-workflow">Create draft</button></div>}
    >
      <Field label="Product" required>
        {(id) => (
          <select id={id} className="input" value={productSlug} onChange={(e) => { const p = lender.products.find((x) => x.slug === e.target.value)!; setProductSlug(p.slug); const n = flowNameFor(lender.slug, p); setName(n); setVersion(nextFlowVersion(flows, n)); setBatchSel('live'); setFiles([]) }}>
            {lender.products.map((p) => <option key={p.slug} value={p.slug}>{p.name} ({p.slug})</option>)}
          </select>
        )}
      </Field>
      <div className="grid grid-cols-[1fr_120px] gap-3">
        <Field label="Workflow name" required help="One workflow = one endpoint">
          {(id) => <input id={id} className="input mono" value={name} onChange={(e) => { setName(e.target.value); setVersion(nextFlowVersion(flows, e.target.value)) }} />}
        </Field>
        <Field label="Version" required>
          {(id) => <input id={id} className="input mono" value={version} onChange={(e) => setVersion(e.target.value)} />}
        </Field>
      </div>
      <Field label="Uses API batch" help="The live batch moves with C1; a pinned draft batch is for UAT only until it is built">
        {(id) => (
          <select id={id} className="input mono" value={batchSel} onChange={(e) => { setBatchSel(e.target.value); setFiles([]) }}>
            {live && <option value="live">the live batch ({live.version})</option>}
            {mineBatches.map((b) => <option key={b.id} value={b.version}>pinned {b.version} ({b.status.replace('_', ' ')})</option>)}
          </select>
        )}
      </Field>
      <fieldset className="mt-2">
        <legend className="label">Start from</legend>
        <label className="mb-1 flex items-center gap-2 text-xs"><input type="radio" name="wf-start" checked={start === 'copy'} disabled={!mineFlows.length} onChange={() => setStart('copy')} /> a copy of an existing version</label>
        {start === 'copy' && (
          <select className="input mono mb-2" aria-label="Version to copy" value={fromId} onChange={(e) => setFromId(e.target.value)}>
            {mineFlows.map((f) => <option key={f.id} value={f.id}>{f.name} {f.version} ({f.status.replace('_', ' ')})</option>)}
          </select>
        )}
        <label className="mb-1 flex items-center gap-2 text-xs"><input type="radio" name="wf-start" checked={start === 'blank'} onChange={() => setStart('blank')} /> Start + these endpoints in a row (every other port to an Answer)</label>
        {start === 'blank' && (
          <div className="flex flex-wrap gap-2 pl-5">
            {callable.map((f) => (
              <label key={f} className="flex items-center gap-1 font-mono text-2xs">
                <input type="checkbox" checked={files.includes(f)} onChange={(e) => setFiles(e.target.checked ? [...files, f] : files.filter((x) => x !== f))} /> {f}
              </label>
            ))}
            {files.length > 0 && <p className="w-full text-2xs text-muted">Order: {files.map(endpointLabel).join(' → ')}</p>}
          </div>
        )}
      </fieldset>
      {error && <p className="field-error">{error}</p>}
    </Drawer>
  )
}
